import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { pm, getFileUrl } from '../../services/api';
import { theme } from '../../theme';
import {
  LayoutDashboard, CircleDot, RefreshCw, Layers, Flag,
  Users, Bug, FlaskConical, Clock, Settings, ChevronRight,
} from 'lucide-react';
import ProjectDashboard from './ProjectDashboard';
import Issues          from './Issues';
import Sprints         from './Sprints';
import Modules         from './Modules';
import Milestones      from './Milestones';
import Members         from './Members';
import ProjectSettings from './ProjectSettings';
import WorkHours       from './WorkHours';
import Bugs            from './Bugs';
import QA              from './QA';

const STATUS_META = {
  planned:   { label: 'Planned',   color: '#6B7280', bg: '#F3F4F6' },
  active:    { label: 'Active',    color: '#16A34A', bg: '#DCFCE7' },
  on_hold:   { label: 'On Hold',   color: '#D97706', bg: '#FEF3C7' },
  completed: { label: 'Completed', color: '#2563EB', bg: '#DBEAFE' },
  cancelled: { label: 'Cancelled', color: '#DC2626', bg: '#FEE2E2' },
};

const ACCENTS = ['#F97316','#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B','#22C55E','#EF4444'];
const accent  = (name = '') => ACCENTS[name.charCodeAt(0) % ACCENTS.length];

const NAV_TABS = [
  { key: 'dashboard',  label: 'Dashboard',  Icon: LayoutDashboard },
  { key: 'issues',     label: 'Issues',     Icon: CircleDot       },
  { key: 'cycles',     label: 'Sprints',    Icon: RefreshCw       },
  { key: 'modules',    label: 'Modules',    Icon: Layers          },
  { key: 'milestones', label: 'Milestones', Icon: Flag            },
  { key: 'members',    label: 'Members',    Icon: Users           },
  { key: 'bugs',       label: 'Bugs',       Icon: Bug             },
  { key: 'qa',         label: 'QA',         Icon: FlaskConical    },
  { key: 'workhours',  label: 'Work Hours', Icon: Clock           },
  { key: 'settings',   label: 'Settings',   Icon: Settings        },
];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate      = useNavigate();
  const location      = useLocation();

  const hashTab    = location.hash.replace('#', '');
  const [activeTab, setActiveTab] = useState(
    NAV_TABS.some(t => t.key === hashTab) ? hashTab : 'dashboard'
  );
  const [project, setProject] = useState(null);
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  const switchTab = (key) => {
    setActiveTab(key);
    navigate(`/projects/${projectId}#${key}`, { replace: true });
  };

  const loadProject = useCallback(() => {
    setLoading(true);
    Promise.all([
      pm.getProject(projectId),
      pm.getProjectDashboard(projectId),
    ]).then(([pRes, dRes]) => {
      setProject(pRes.data);
      setStats(dRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { loadProject(); }, [loadProject]);

  // Sync active tab when navigating here from a notification deep-link (hash changes)
  useEffect(() => {
    const h = location.hash.replace('#', '');
    if (h && NAV_TABS.some(t => t.key === h)) setActiveTab(h);
  }, [location.hash]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:320,
                  flexDirection:'column', gap:'0.875rem', color:'#9CA3AF' }}>
      <div style={{ width:32, height:32, border:'2.5px solid #E4E4E7', borderTopColor:theme.primary,
                    borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
      <span style={{ fontSize:'0.82rem', fontWeight:500 }}>Loading project…</span>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!project) return (
    <div style={{ textAlign:'center', padding:'4rem', color:'#6B7280' }}>
      <p style={{ fontWeight:600, color:'#111827', fontSize:'0.95rem' }}>Project not found</p>
      <button onClick={() => navigate('/projects')}
        style={{ marginTop:'1rem', padding:'0.45rem 1.25rem', background:theme.primary,
                 color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:'0.82rem' }}>
        ← Back to Projects
      </button>
    </div>
  );

  const sm  = STATUS_META[project.status] || STATUS_META.active;
  const a   = accent(project.name);
  const pct = stats?.completion_pct ?? 0;

  return (
    <div style={{ maxWidth:1400, display:'flex', flexDirection:'column', height:'100%' }}>

      {/* ── Header ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E4E4E7' }}>

        {/* Top row: breadcrumb + stats */}
        <div style={{ padding:'0.875rem 1.5rem 0', display:'flex', alignItems:'center',
                      justifyContent:'space-between', flexWrap:'wrap', gap:'0.625rem' }}>

          {/* Left: breadcrumb + project name */}
          <div style={{ display:'flex', alignItems:'center', gap:'0.375rem', minWidth:0 }}>
            <button onClick={() => navigate('/projects')}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF',
                       fontSize:'0.78rem', fontWeight:500, padding:0,
                       transition:'color 0.12s' }}
              onMouseEnter={e => e.currentTarget.style.color = theme.primary}
              onMouseLeave={e => e.currentTarget.style.color = '#9CA3AF'}>
              Projects
            </button>
            <ChevronRight size={12} color="#D1D5DB" />
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', minWidth:0 }}>
              {project.logo
                ? <img src={getFileUrl(project.logo)} alt={project.name}
                    style={{ width:28, height:28, borderRadius:6, objectFit:'contain', flexShrink:0, border:'1px solid #E4E4E7', background:'#fff' }} />
                : <div style={{ width:24, height:24, borderRadius:6, background:`${a}15`, color:a,
                                fontWeight:800, fontSize:'0.7rem', display:'flex', alignItems:'center',
                                justifyContent:'center', flexShrink:0, border:`1.5px solid ${a}25` }}>
                    {project.name.charAt(0).toUpperCase()}
                  </div>
              }
              {project.code && (
                <span style={{ fontSize:'0.72rem', color:'#9CA3AF', fontWeight:600,
                               fontFamily:'monospace', flexShrink:0 }}>{project.code}</span>
              )}
              {project.code && <span style={{ color:'#E4E4E7', fontSize:'0.75rem' }}>·</span>}
              <h1 style={{ margin:0, fontSize:'0.95rem', fontWeight:700, color:'#111827',
                           whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                {project.name}
              </h1>
            </div>
          </div>

          {/* Right: status pill + progress + counts */}
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexShrink:0, flexWrap:'wrap' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:sm.bg,
                           color:sm.color, padding:'3px 9px', borderRadius:6,
                           fontSize:'0.67rem', fontWeight:700, letterSpacing:'0.02em' }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:sm.color }} />
              {sm.label}
            </span>
            <div style={{ display:'flex', alignItems:'center', gap:'0.375rem' }}>
              <div style={{ width:56, height:4, background:'#F3F4F6', borderRadius:9999, overflow:'hidden' }}>
                <div style={{ width:`${pct}%`, height:'100%', background:a, borderRadius:9999,
                              transition:'width 0.5s ease' }} />
              </div>
              <span style={{ fontSize:'0.7rem', fontWeight:700, color:'#374151' }}>{pct}%</span>
            </div>
            {stats && (
              <div style={{ display:'flex', gap:'0.25rem' }}>
                {[
                  { v: stats.total_issues ?? 0,     label:'Issues',  color:'#374151', bg:'#F3F4F6' },
                  { v: stats.completed_issues ?? 0, label:'Done',    color:'#16A34A', bg:'#DCFCE7' },
                  { v: stats.overdue_issues ?? 0,   label:'Overdue',
                    color: stats.overdue_issues > 0 ? '#DC2626' : '#6B7280',
                    bg:    stats.overdue_issues > 0 ? '#FEE2E2' : '#F3F4F6' },
                ].map(s => (
                  <div key={s.label}
                    style={{ background:s.bg, color:s.color, borderRadius:5, padding:'3px 8px',
                             fontSize:'0.67rem', fontWeight:700, display:'flex', alignItems:'center', gap:3 }}>
                    {s.v} <span style={{ fontWeight:500, opacity:0.7 }}>{s.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Tab navigation ── */}
        <div style={{ display:'flex', padding:'0 1.25rem', overflowX:'auto', marginTop:'0.375rem',
                      scrollbarWidth:'none' }}>
          {NAV_TABS.map(({ key, label, Icon }) => {
            const isActive = activeTab === key;
            return (
              <button key={key} onClick={() => switchTab(key)}
                style={{ display:'flex', alignItems:'center', gap:'0.35rem',
                         padding:'0.55rem 0.875rem', border:'none',
                         borderBottom:`2px solid ${isActive ? a : 'transparent'}`,
                         background:'transparent', cursor:'pointer',
                         fontSize:'0.78rem', fontWeight: isActive ? 600 : 400,
                         color: isActive ? a : '#6B7280',
                         whiteSpace:'nowrap', transition:'all 0.12s',
                         marginBottom:-1, flexShrink:0 }}
                onMouseEnter={e => { if (!isActive) { e.currentTarget.style.color='#374151'; e.currentTarget.style.borderBottomColor='#E4E4E7'; }}}
                onMouseLeave={e => { if (!isActive) { e.currentTarget.style.color='#6B7280'; e.currentTarget.style.borderBottomColor='transparent'; }}}>
                <Icon size={14} strokeWidth={isActive ? 2.5 : 1.75} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tab content ── */}
      <div style={{ flex:1, background:'#F8FAFC', minHeight:0, overflowY:'auto' }}>
        {activeTab === 'dashboard'  && <ProjectDashboard project={project} stats={stats} />}
        {activeTab === 'issues'     && <Issues          project={project} onRefresh={loadProject} />}
        {activeTab === 'cycles'     && <Sprints         project={project} onRefresh={loadProject} />}
        {activeTab === 'modules'    && <Modules         project={project} onRefresh={loadProject} />}
        {activeTab === 'milestones' && <Milestones      project={project} onRefresh={loadProject} />}
        {activeTab === 'members'    && <Members         project={project} />}
        {activeTab === 'bugs'       && <Bugs            project={project} />}
        {activeTab === 'qa'         && <QA              project={project} />}
        {activeTab === 'workhours'  && <WorkHours       project={project} />}
        {activeTab === 'settings'   && <ProjectSettings project={project} onRefresh={loadProject} />}
      </div>
    </div>
  );
}
